import { AuthClient, type GoTrueClient } from '@supabase/auth-js';
import { API_BASE } from './apiBase.ts';
import {
  clearCachedConfig,
  clearReloadFlag,
  readCachedConfig,
  reloadOnce,
  sameBootTarget,
  usableReloadGuard,
  writeCachedConfig,
  type ConfigStorage,
  type ReloadGuardStorage,
} from './runtimeConfigCache.ts';
import {
  parseConfigResponse,
  purgeStaleAuthKeys,
  storageKeyFor,
  type Audience,
  type HostMode,
  type RuntimeSupabaseConfig,
} from './supabaseConfig.ts';

export { adminBasename, type Audience, type HostMode } from './supabaseConfig.ts';

const envUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
const envPublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '';

/**
 * What every getter below returns: an object whose `.auth` is the GoTrue
 * client. The browser only ever talks to Supabase AUTH (every data read goes
 * through our API), so the SPAs build @supabase/auth-js's client directly
 * rather than supabase-js's createClient — which also bundled the PostgREST,
 * Storage, Realtime (+ phoenix) and Functions clients that nothing here calls.
 * Wrapping it as `{ auth }` keeps every `getSupabase().auth.…` call site, and
 * every structural `AuthLike` type in the apps, compiling unchanged.
 */
// GoTrueClient is the class; AuthClient is the same class under its public alias.
export type AuthOnlyClient = { readonly auth: GoTrueClient };

type AuthOptions = {
  persistSession: boolean;
  autoRefreshToken: boolean;
  detectSessionInUrl: boolean;
  storageKey: string;
};

/**
 * Build the auth client EXACTLY as supabase-js 2.105.4's createClient built it,
 * so a deploy of this change signs nobody out and changes no behaviour.
 *
 * Mirrored from SupabaseClient's constructor + _initSupabaseAuthClient:
 *  - url `<project>/auth/v1`, resolved the same way (trailing slash, then a
 *    relative URL), so GoTrue sees identical endpoints.
 *  - headers `apikey` + `Authorization: Bearer <publishable key>`. supabase-js
 *    also sent `X-Client-Info: supabase-js-web/<ver>` — a telemetry label only,
 *    deliberately not forged here.
 *  - storageKey, persistSession, autoRefreshToken, detectSessionInUrl passed
 *    straight through. The storageKey IS the session's identity: the stored
 *    value under it is written by this very same GoTrueClient code, so the
 *    format cannot differ — only the key could, and supabase.test.ts pins it.
 *  - flowType 'implicit': supabase-js's own default, which magic links rely on
 *    (the /c/callback hash). auth-js defaults to it too; stated so a future
 *    default change cannot slip in.
 *  - no `storage`/`lock`: both libraries leave them unset, so GoTrue picks
 *    localStorage and the navigator.locks lock in the browser either way.
 *  - lockAcquireTimeout -1: supabase-js forwarded its OWN undefined, which
 *    overwrote auth-js's 5000ms default, and navigatorLock treats undefined as
 *    "no timeout" (only `> 0` arms the timer and lock-steal recovery). -1 is
 *    that same "wait, never steal" behaviour spelled out; omitting the option
 *    would quietly turn on a 5s steal between tabs refreshing the session.
 */
function createAuthOnlyClient(url: string, key: string, options: AuthOptions): AuthOnlyClient {
  const base = new URL(url.trim().endsWith('/') ? url.trim() : `${url.trim()}/`);
  return {
    auth: new AuthClient({
      url: new URL('auth/v1', base).href,
      headers: { Authorization: `Bearer ${key}`, apikey: key },
      storageKey: options.storageKey,
      persistSession: options.persistSession,
      autoRefreshToken: options.autoRefreshToken,
      detectSessionInUrl: options.detectSessionInUrl,
      flowType: 'implicit',
      lockAcquireTimeout: -1,
    }),
  };
}

let runtime: RuntimeSupabaseConfig | null = null;
let initStarted = false;
let initPromise: Promise<void> | null = null;
let revalidation: Promise<void> | null = null;

let companyClient: AuthOnlyClient | null = null;
let adminClient: AuthOnlyClient | null = null;
let opsClient: AuthOnlyClient | null = null;

/** The browser surfaces the boot gate touches, injectable for tests. */
export type BootDeps = {
  origin: string;
  storage: ConfigStorage | null;
  session: ReloadGuardStorage | null;
  reload: () => void;
};

function browserBootDeps(): BootDeps {
  // Every access guarded: in node (tests) there is no window, and in a
  // sandboxed iframe or with blocked site data merely touching storage throws.
  let storage: ConfigStorage | null = null;
  let session: ReloadGuardStorage | null = null;
  let origin = '';
  try {
    if (typeof window !== 'undefined') {
      origin = window.location?.origin ?? '';
      storage = window.localStorage ?? null;
    }
  } catch {
    storage = null;
  }
  try {
    // usableReloadGuard, not just "exists": a sessionStorage that is there
    // but refuses writes (quota, older Safari private mode) would let a
    // cached boot render and then silently refuse its correcting reload.
    if (typeof window !== 'undefined') session = usableReloadGuard(window.sessionStorage);
  } catch {
    session = null;
  }
  return {
    origin,
    storage,
    session,
    reload: () => window.location.reload(),
  };
}

function purgeStaleKeysFor(ref: string | null): void {
  try {
    purgeStaleAuthKeys(window.localStorage, ref);
  } catch {
    // storage (or window) unavailable — stale keys just aren't purged
  }
}

// GET /api/config, parsed. Throws a user-legible Error on every failure.
async function fetchRuntimeConfig(fetchFn: typeof fetch): Promise<RuntimeSupabaseConfig> {
  // 10s cap: a connection refusal or a non-2xx both reject or return
  // promptly on their own, but a STALLED API would otherwise leave the
  // user on an empty #root forever with no retry affordance — the
  // portal is phone-used, where stalls are the common failure mode.
  let res: Response;
  try {
    res = await fetchFn(`${API_BASE}/api/config`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(10_000),
    });
  } catch (err) {
    if (
      err instanceof DOMException &&
      (err.name === 'TimeoutError' || err.name === 'AbortError')
    ) {
      throw new ConfigTimeoutError(
        'Could not load the app configuration: the request took too long.',
      );
    }
    throw err;
  }
  if (!res.ok) {
    // The API's envelope is `{ error: { code, message, request_id } }`
    // (see errorHandler.ts) — a suspended tenant gets a branded message
    // ("This workspace has been suspended.") that CORS deliberately
    // lets through; fall back to a generic status-based string only
    // when the body isn't that shape.
    const body = (await res.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(
      body?.error?.message ??
        `Could not load the app configuration (/api/config returned ${res.status}).`,
    );
  }
  return parseConfigResponse(await res.json());
}

// The request never got an answer in time. A plain Error to every caller; the
// class only lets revalidate() tell "no answer" from "answered, but no".
class ConfigTimeoutError extends Error {}

/**
 * The background half of a cached boot: fetch the live config and compare.
 *
 * WHY RENDERING FROM A CACHED CONFIG IS SAFE against the cell-migration
 * "fail closed" rule (see initSupabaseConfig). That rule exists so a browser
 * never MINTS a session in the wrong auth pool. A cached config can be stale
 * only across a migration (or a rollback, or a hostname change), and then:
 *  - at most one render runs on it. This check lands about a second later,
 *    sees the project changed, drops the cache and reloads; the reloaded boot
 *    is COLD and blocks on the live answer exactly as before this cache.
 *  - in that one render the client holds the OLD pool's session, whose tokens
 *    the API refuses for this tenant anyway (it verifies them against the
 *    tenant's current project), so nothing is readable through it.
 *  - stale auth keys are NOT purged on a cached boot until this check confirms
 *    the ref — purging against a stale ref could delete the session of the
 *    project the user really belongs to.
 * A tenant the API now REFUSES (suspended, unknown host) gets the same: drop
 * the cache, reload, and the cold boot renders today's branded boot error. Only
 * a request that never got an answer (offline, timeout) keeps the cached
 * render — the app's own API calls fail visibly in that case.
 */
async function revalidate(
  fetchFn: typeof fetch,
  cached: RuntimeSupabaseConfig,
  deps: BootDeps,
): Promise<void> {
  let fresh: RuntimeSupabaseConfig;
  try {
    fresh = await fetchRuntimeConfig(fetchFn);
  } catch (err) {
    // A refusal (non-2xx) or a malformed body: the API answered, and the
    // cached copy can no longer be trusted. A network failure (fetch rejects
    // with a TypeError) or a timeout is no answer at all and says nothing.
    const unanswered = err instanceof TypeError || err instanceof ConfigTimeoutError;
    if (!unanswered) {
      clearCachedConfig(deps.storage, deps.origin);
      reloadOnce(deps.session, deps.reload);
    }
    return;
  }
  if (!sameBootTarget(cached, fresh)) {
    clearCachedConfig(deps.storage, deps.origin);
    reloadOnce(deps.session, deps.reload);
    return;
  }
  // Same project and routing: refresh the copy (signInUrls may have moved),
  // and only now — with the ref confirmed — purge the other pools' keys.
  runtime = fresh;
  writeCachedConfig(deps.storage, deps.origin, fresh);
  clearReloadFlag(deps.session);
  purgeStaleKeysFor(fresh.ref);
}

// One-time boot gate (Phase 3b), awaited by the admin and portal SPAs before
// their first render. Mirrors what 3a did on the server: the async work is
// hoisted to a single resolution point so the three getters below stay
// synchronous at every call site.
//
// `vite dev` keeps the build-time VITE_* values when both are set, so local
// dev needs no Worker round-trip. Deployed builds always fetch /api/config —
// and a failure THROWS (the SPA renders a boot error) rather than falling back
// to the VITE_* values, because a dedicated tenant's browser silently falling
// back to the shared project would mint sessions in the wrong auth pool: the
// exact failure the per-project storage keys exist to prevent.
//
// Since the boot answer is cached per origin (runtimeConfigCache.ts), a REPEAT
// visit resolves immediately from the cached copy and revalidates behind the
// render — see revalidate() for why that keeps the fail-closed guarantee. A
// cold visit (nothing cached, or an unreadable cache) blocks exactly as before.
//
// A rejection clears the memo so a retry can re-fetch; the initStarted latch
// stays down, and the two singletons were dropped when the boot began, so the
// getters keep failing closed until a boot succeeds.
export function initSupabaseConfig(
  fetchFn: typeof fetch = fetch,
  deps: BootDeps = browserBootDeps(),
): Promise<void> {
  initStarted = true;
  if (!initPromise) {
    // A getter that ran BEFORE this boot began already cached a client on the
    // build-time SHARED project. Drop it here, not only on success below: the
    // getters short-circuit on a non-null singleton and never reach
    // currentConfig(), so a boot that REJECTS would otherwise leave that
    // wrong-pool client live and serving — the one path through this module
    // that fails open. Deliberately inside the memo guard: a repeat call after
    // a SUCCESSFUL boot must not churn clients that components already hold.
    companyClient = null;
    adminClient = null;
    initPromise = (async () => {
      let confirmed = true;
      if (import.meta.env.DEV && envUrl && envPublishableKey) {
        // 'audiences' in dev, unlike parseConfigResponse's 'legacy' default:
        // here we KNOW the origin (a Vite dev server on its own port) owns its
        // whole path space, so the short paths are both safe and what the
        // deployed target state uses. A developer who needs to exercise legacy
        // routing unsets the VITE_* pair and boots against the real API.
        // audience null, NOT a guess. Local dev serves every audience from one
        // Vite origin, so closing the portal's path gate here would 404 /c/*,
        // /d/* and /visit* the moment a developer opened them.
        runtime = {
          url: envUrl,
          publishableKey: envPublishableKey,
          ref: null,
          hostMode: 'audiences',
          audience: null,
          signInUrls: null,
        };
      } else {
        // The cached (non-blocking) boot is only safe when its correction —
        // the one-shot reload in revalidate() — can actually happen, and that
        // needs sessionStorage for the loop guard. Without it a migrated
        // tenant would keep the stale project for the whole tab and could
        // sign in against the wrong pool, so it boots cold and blocks, as
        // before the cache existed.
        const cached = deps.session ? readCachedConfig(deps.storage, deps.origin) : null;
        if (cached) {
          runtime = cached;
          confirmed = false;
          revalidation = revalidate(fetchFn, cached, deps);
        } else {
          runtime = await fetchRuntimeConfig(fetchFn);
          writeCachedConfig(deps.storage, deps.origin, runtime);
          clearReloadFlag(deps.session);
        }
      }
      // A getter that RACED the boot gate took rule 3 in currentConfig() and
      // cached a client built from the build-time SHARED project under the
      // legacy bare key. Dropping the two singletons here makes the next call
      // rebuild against the resolved config — otherwise a dedicated tenant
      // keeps a client in the wrong auth pool by the one path that never
      // throws. opsClient is deliberately left alone: it is built from the
      // build-time values and is never a function of the runtime config.
      companyClient = null;
      adminClient = null;
      // A cached boot purges in revalidate(), once the ref is confirmed.
      if (confirmed) purgeStaleKeysFor(runtime.ref);
    })().catch((err: unknown) => {
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

function assertEnvConfig(): void {
  if (!envUrl || !envPublishableKey) {
    throw new Error(
      'Supabase client unavailable: set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY',
    );
  }
}

function currentConfig(): RuntimeSupabaseConfig {
  if (runtime) return runtime;
  if (initStarted) {
    // A getter ran before (or after a failed) boot. Falling back to the VITE_*
    // values here would silently hand a dedicated tenant the shared auth pool,
    // so this is a hard error; the boot gate in main.tsx prevents it entirely.
    throw new Error('Supabase client requested before initSupabaseConfig() resolved');
  }
  // No boot at all (ops console, unit tests): build-time env, as before 3b.
  assertEnvConfig();
  // No boot gate ran, so nothing told us the mode. 'legacy' for the same
  // reason parseConfigResponse defaults to it: the recoverable wrong guess.
  return { url: envUrl, publishableKey: envPublishableKey, ref: null, hostMode: 'legacy', audience: null, signInUrls: null };
}

// Company portal session (magic-link redeem flow). Distinct storage key so
// admin and company sessions can coexist in the same browser — and since 3b
// the key is namespaced per Supabase PROJECT (storageKeyFor), so a tenant's
// shared→dedicated migration orphans the old pool's session and lands the
// user on a clean login instead of an error loop.
export function getSupabase(): AuthOnlyClient {
  if (!companyClient) {
    const cfg = currentConfig();
    companyClient = createAuthOnlyClient(cfg.url, cfg.publishableKey, {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: storageKeyFor('l2u-company-auth', cfg.ref),
    });
  }
  return companyClient;
}

// Admin portal session (email + password). Separate storage key.
// detectSessionInUrl must stay false: admins sign in via password, and if
// this is true the admin client steals the company magic-link hash on
// /c/callback and later signs out, revoking the shared refresh token.
export function getAdminSupabase(): AuthOnlyClient {
  if (!adminClient) {
    const cfg = currentConfig();
    adminClient = createAuthOnlyClient(cfg.url, cfg.publishableKey, {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: storageKeyFor('l2u-admin-auth', cfg.ref),
    });
  }
  return adminClient;
}

/**
 * A throwaway admin-pool client whose session lives in MEMORY only.
 *
 * For /reset-password and /accept-invite: a recovery or invite link's redeemed
 * session must not be written to the admin client's localStorage (where every
 * later page load would find a signed-in admin) until the new password has
 * actually been set. The page redeems and calls updateUser() through this
 * client, and only then hands the session to getAdminSupabase() via
 * setSession() (apps/admin lib/auth/oneTimeLinkSession.ts). Nothing here persists,
 * refreshes on a timer, or reads the URL, and the storage key is distinct so
 * GoTrue does not warn about two clients sharing the admin key.
 *
 * Deliberately NOT memoised: each attempt gets a fresh client.
 */
export function createEphemeralAdminSupabase(): AuthOnlyClient {
  const cfg = currentConfig();
  return createAuthOnlyClient(cfg.url, cfg.publishableKey, {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: cfg.ref ? `l2u-admin-reset.${cfg.ref}` : 'l2u-admin-reset',
  });
}

// Ops console (super-admin) session (email + password). Third storage key so an
// ops session never collides with an admin or company session in the same
// browser. Like the admin client, detectSessionInUrl stays false — ops signs in
// with a password and must not steal another portal's magic-link hash.
//
// NEVER repointed at a cell: the ops console is platform-level — it manages
// tenants and reads control.* — so it uses the build-time shared-project values
// directly and skips currentConfig() on purpose. Its key is never namespaced.
export function getOpsSupabase(): AuthOnlyClient {
  if (!opsClient) {
    assertEnvConfig();
    opsClient = createAuthOnlyClient(envUrl, envPublishableKey, {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: 'l2u-ops-auth',
    });
  }
  return opsClient;
}

// The hostname layout this origin booted against. Read by the admin SPA to
// choose its router basename, which must be decided BEFORE the first render —
// main.tsx already awaits initSupabaseConfig() for exactly that reason.
export function getHostMode(): HostMode {
  return currentConfig().hostMode;
}

/**
 * Which audience this origin serves, or null when the host names none.
 *
 * Read by the portal to refuse the path families that are not its own — one
 * bundle answers /c/*, /d/* and /visit* on three hostnames, so without this
 * /d/login resolved on the company host. Like getHostMode it throws until the
 * boot gate has resolved, which is why App reads it after main.tsx awaits.
 *
 * Null means OPEN, not unknown-and-suspicious: the legacy shared base serves
 * every audience at once, and so does local dev.
 */
export function getHostAudience(): Audience | null {
  return currentConfig().audience;
}

/**
 * This tenant's company and driver sign-in pages when they live on other
 * hostnames, or null when a relative link reaches them. See
 * RuntimeSupabaseConfig.signInUrls.
 */
export function getSignInUrls(): { company: string; driver: string } | null {
  return currentConfig().signInUrls;
}

// Test hook: the background revalidation a cached boot started, if any.
export function __configRevalidation(): Promise<void> | null {
  return revalidation;
}

// Test hook, matching __clearHostCache / __clearProjectCache in the API.
export function __resetSupabaseState(): void {
  runtime = null;
  initStarted = false;
  initPromise = null;
  revalidation = null;
  companyClient = null;
  adminClient = null;
  opsClient = null;
}
